import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Loader2, Download, ChevronDown, ChevronRight, BarChart3, ExternalLink,
} from 'lucide-react';
import { format, subDays } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PRESETS = [
  { id: '7d', label: 'Last 7 days', days: 7 },
  { id: '30d', label: 'Last 30 days', days: 30 },
  { id: '90d', label: 'Last 90 days', days: 90 },
  { id: 'ytd', label: 'YTD', days: null },
];

const todayISO = () => format(new Date(), 'yyyy-MM-dd');
const daysAgoISO = (n) => format(subDays(new Date(), n), 'yyyy-MM-dd');
const ytdStartISO = () => `${new Date().getFullYear()}-01-01`;

const fmtH = (h) => `${(h || 0).toFixed(1)}h`;
const fmtUSD = (v) =>
  (v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

export default function HoursByJob() {
  const navigate = useNavigate();
  const [preset, setPreset] = useState('30d');
  const [start, setStart] = useState(daysAgoISO(30));
  const [end, setEnd] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [sortBy, setSortBy] = useState('hours'); // hours | cost | jobs

  const applyPreset = (id) => {
    setPreset(id);
    const p = PRESETS.find(x => x.id === id);
    if (!p) return;
    setEnd(todayISO());
    if (p.id === 'ytd') setStart(ytdStartISO());
    else setStart(daysAgoISO(p.days));
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/timesheets/reports/hours-by-job`, {
        params: { start_date: start, end_date: end },
      });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load report');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [start, end]);

  useEffect(() => { load(); }, [load]);

  const sortedClients = (data?.by_client || []).slice().sort((a, b) => {
    if (sortBy === 'cost') return (b.total_cost || 0) - (a.total_cost || 0);
    if (sortBy === 'jobs') return (b.job_count || 0) - (a.job_count || 0);
    return (b.total_hours || 0) - (a.total_hours || 0);
  });

  const toggle = (clientKey) => setExpanded(s => ({ ...s, [clientKey]: !s[clientKey] }));

  const downloadCSV = () => {
    if (!data) return;
    const rows = [
      [`Hours by Job — ${start} to ${end}`],
      [`Total Hours: ${(data.totals.total_hours || 0).toFixed(2)}`, `Labor Cost: $${(data.totals.total_cost || 0).toFixed(2)}`, `Jobs: ${data.totals.job_count}`, `Clients: ${data.totals.client_count}`],
      [],
      ['Client', 'Job #', 'Job Title', 'Hours', 'Est Hours', 'Variance', 'Labor $/hr', 'Labor Cost'],
    ];
    sortedClients.forEach(c => {
      c.jobs.forEach(j => {
        rows.push([
          c.client_name,
          j.job_number || '',
          j.job_title || '',
          j.hours.toFixed(2),
          j.estimated_hours || 0,
          j.variance_hours.toFixed(2),
          j.labor_cost_per_hour || 0,
          j.labor_cost.toFixed(2),
        ]);
      });
    });
    const csv = rows.map(r => r.map(v => {
      const s = String(v ?? '');
      const safe = /^[=+\-@]/.test(s) ? `'${s}` : s;
      return /[",\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
    }).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hours_by_job_${start}_to_${end}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success('CSV downloaded');
  };

  return (
    <div className="space-y-4 max-w-[1400px] mx-auto px-3 md:px-6 py-4" data-testid="hours-by-job-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 font-['Barlow_Condensed'] leading-none tracking-tight flex items-center gap-3">
            <BarChart3 className="w-8 h-8 text-amber-500" />
            Hours by Job
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Owner-grade view of where the team's time went · grouped by client.
          </p>
        </div>
        <Button
          onClick={downloadCSV}
          disabled={!data || loading}
          className="bg-amber-500 hover:bg-amber-600 text-slate-900 h-9"
          data-testid="hours-by-job-export-btn"
        >
          <Download className="w-4 h-4 mr-1" /> Export CSV
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-wrap gap-1.5">
              {PRESETS.map(p => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => applyPreset(p.id)}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${
                    preset === p.id
                      ? 'bg-slate-900 text-white border-slate-900'
                      : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                  }`}
                  data-testid={`preset-${p.id}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="flex items-end gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Start</label>
                <Input
                  type="date"
                  value={start}
                  onChange={e => { setStart(e.target.value); setPreset('custom'); }}
                  className="h-9 w-[150px]"
                  data-testid="hours-start-input"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">End</label>
                <Input
                  type="date"
                  value={end}
                  onChange={e => { setEnd(e.target.value); setPreset('custom'); }}
                  className="h-9 w-[150px]"
                  data-testid="hours-end-input"
                />
              </div>
            </div>
            <div className="ml-auto flex items-end gap-2">
              <label className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold flex flex-col">
                Sort
                <select
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value)}
                  className="mt-0.5 h-9 border border-slate-200 rounded-md px-2 text-sm bg-white"
                  data-testid="hours-sort-select"
                >
                  <option value="hours">Most hours</option>
                  <option value="cost">Highest labor $</option>
                  <option value="jobs">Most jobs</option>
                </select>
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Totals */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="hours-totals">
          <Card>
            <CardContent className="py-4">
              <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Total Hours</p>
              <p className="text-3xl font-bold text-slate-900 leading-none mt-1">
                {fmtH(data.totals.total_hours)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4">
              <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Labor Cost</p>
              <p className="text-3xl font-bold text-emerald-700 leading-none mt-1">
                {fmtUSD(data.totals.total_cost)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4">
              <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Jobs</p>
              <p className="text-3xl font-bold text-slate-900 leading-none mt-1">
                {data.totals.job_count}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4">
              <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Clients</p>
              <p className="text-3xl font-bold text-slate-900 leading-none mt-1">
                {data.totals.client_count}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Per-Client breakdown */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-xl font-['Barlow_Condensed']">By Client → Job</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin mx-auto" />
            </div>
          ) : sortedClients.length === 0 ? (
            <p className="p-10 text-center text-slate-400 text-sm">
              No time logged in this date range.
            </p>
          ) : (
            <div className="divide-y divide-slate-100">
              {sortedClients.map(c => {
                const key = c.client_id || c.client_name;
                const isOpen = expanded[key];
                return (
                  <div key={key} data-testid={`client-row-${key}`}>
                    <button
                      type="button"
                      onClick={() => toggle(key)}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-amber-50/40 text-left"
                    >
                      {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-slate-900 truncate">{c.client_name || '(no client)'}</p>
                        <p className="text-xs text-slate-500">{c.job_count} job{c.job_count === 1 ? '' : 's'}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-slate-900">{fmtH(c.total_hours)}</p>
                        {c.total_cost > 0 && (
                          <p className="text-xs text-emerald-700">{fmtUSD(c.total_cost)}</p>
                        )}
                      </div>
                    </button>
                    {isOpen && (
                      <div className="bg-slate-50/60 px-4 py-2">
                        <table className="w-full text-sm">
                          <thead className="text-xs text-slate-500 uppercase">
                            <tr>
                              <th className="text-left py-1.5">Job</th>
                              <th className="text-right py-1.5 hidden md:table-cell">Est</th>
                              <th className="text-right py-1.5">Hours</th>
                              <th className="text-right py-1.5 hidden md:table-cell">Variance</th>
                              <th className="text-right py-1.5">Cost</th>
                              <th className="w-8"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {c.jobs.map(j => {
                              const overBy = j.variance_hours;
                              const variancePill =
                                j.estimated_hours <= 0 ? null
                                : overBy > 0 ? 'text-orange-700 bg-orange-50 border-orange-200'
                                : 'text-emerald-700 bg-emerald-50 border-emerald-200';
                              return (
                                <tr key={j.job_id} className="border-t border-slate-100" data-testid={`job-row-${j.job_id}`}>
                                  <td className="py-2">
                                    <p className="font-mono text-[11px] font-bold text-amber-600">{j.job_number}</p>
                                    <p className="text-slate-700 truncate max-w-[260px]">{j.job_title}</p>
                                  </td>
                                  <td className="text-right py-2 hidden md:table-cell text-slate-500">
                                    {j.estimated_hours > 0 ? fmtH(j.estimated_hours) : '—'}
                                  </td>
                                  <td className="text-right py-2 font-semibold text-slate-900">
                                    {fmtH(j.hours)}
                                  </td>
                                  <td className="text-right py-2 hidden md:table-cell">
                                    {variancePill ? (
                                      <Badge variant="outline" className={variancePill}>
                                        {overBy > 0 ? '+' : ''}{overBy.toFixed(1)}h
                                      </Badge>
                                    ) : (
                                      <span className="text-slate-300">—</span>
                                    )}
                                  </td>
                                  <td className="text-right py-2 text-slate-700">
                                    {j.labor_cost > 0 ? fmtUSD(j.labor_cost) : <span className="text-slate-300">—</span>}
                                  </td>
                                  <td className="py-2">
                                    <button
                                      type="button"
                                      onClick={() => navigate(`/jobs/${j.job_id}`)}
                                      className="text-amber-600 hover:text-amber-700"
                                      title="Open job"
                                      data-testid={`open-job-${j.job_id}`}
                                    >
                                      <ExternalLink className="w-4 h-4" />
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
